import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GameProvider, useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import { BookOpen, Swords, Backpack, Map, ArrowLeft, Heart, Sparkles, Trophy, Skull, Crown, Coins, BarChart3, Handshake, Library, ScrollText, Shield, MoreHorizontal, AlertTriangle } from "lucide-react";
import StoryBook from "@/components/game/StoryBook";
import BattleArena from "@/components/game/BattleArena";
import Inventory from "@/components/game/Inventory";
import WorldMap from "@/components/game/WorldMap";
import Achievements from "@/components/game/Achievements";
import Bestiary from "@/components/game/Bestiary";
import Shop from "@/components/game/Shop";
import SettingsPanel from "@/components/game/SettingsPanel";
import LevelUpModal from "@/components/game/LevelUpModal";
import DailyQuests from "@/components/game/DailyQuests";
import CardRevealOverlay from "@/components/game/CardRevealOverlay";
import OnboardingTour, { hasSeenOnboarding } from "@/components/game/OnboardingTour";
import AIStatusBanner from "@/components/game/AIStatusBanner";
import Dashboard from "@/components/game/Dashboard";
import Trade from "@/components/game/Trade";
import Collection from "@/components/game/Collection";
import Grimoire from "@/components/game/Grimoire";
import Arena from "@/components/game/Arena";
import NyxMascot from "@/components/game/NyxMascot";
import Fusion from "@/components/game/Fusion";
import { t, tf } from "@/utils/i18n";
import ambientMusic from "@/utils/AmbientMusic";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

function GameContent() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { game, loadGame, setGame, error, loading } = useGame();
  const { token, API } = useAuth();
  const [activeTab, setActiveTab] = useState("story");
  const [moreOpen, setMoreOpen] = useState(false);
  const [counts, setCounts] = useState({ trade: 0, grimoire: 0, inventory: 0, more: 0, total: 0 });
  const [dailyLimit, setDailyLimit] = useState(null);
  const [revealQueue, setRevealQueue] = useState([]);
  const [pendingTrade, setPendingTrade] = useState(null); // {card_id, category} to prefill Trade form
  const [showOnboarding, setShowOnboarding] = useState(false);
  const seenCardIdsRef = React.useRef(null); // null until first load
  const primedRef = React.useRef(false);

  useEffect(() => {
    loadGame(gameId);
  }, [gameId, loadGame]);

  // Poll notification counters for tab badges. Refresh every 25s while the
  // game tab is active. Also refresh on tab focus.
  const refreshCounts = React.useCallback(async () => {
    if (!gameId || !token) return;
    try {
      const r = await axios.get(`${API}/notifications/counts`, {
        params: { game_id: gameId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCounts(r.data || { trade: 0, grimoire: 0, inventory: 0, more: 0, total: 0 });
    } catch { /* silent — non-critical */ }
  }, [gameId, token, API]);

  useEffect(() => {
    refreshCounts();
    const i = setInterval(refreshCounts, 25000);
    const onFocus = () => refreshCounts();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(i); window.removeEventListener("focus", onFocus); };
  }, [refreshCounts]);

  // Mark grimoire as "seen" when the user opens the Grimoire tab. Clears
  // the badge instantly and persists last_grimoire_seen_at server-side.
  useEffect(() => {
    if (activeTab !== "grimoire") return;
    if (!token) return;
    setCounts(c => ({ ...c, grimoire: 0, more: Math.max(0, c.more - c.grimoire), total: Math.max(0, c.total - c.grimoire) }));
    axios.post(`${API}/notifications/grimoire/seen`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [activeTab, token, API]);

  // Mark inventory cards as seen when the Inventory tab opens. Uses a small
  // delay so the player actually SEES the badge flash before it's cleared.
  useEffect(() => {
    if (activeTab !== "inventory") return;
    if (!token || !gameId) return;
    const timeout = setTimeout(() => {
      setCounts(c => ({ ...c, inventory: 0, total: Math.max(0, c.total - c.inventory) }));
      axios.post(`${API}/notifications/inventory/seen`, null, {
        params: { game_id: gameId },
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timeout);
  }, [activeTab, token, API, gameId]);

  // First-time onboarding: show once per browser after the game loads and
  // the HUD has mounted. Re-openable from Settings via the `ac:show-onboarding`
  // custom event.
  useEffect(() => {
    if (!game) return;
    if (hasSeenOnboarding()) return;
    const t = setTimeout(() => setShowOnboarding(true), 600);
    return () => clearTimeout(t);
    // We only want to re-run this when the *identity* of the game changes
    // (e.g., switching saves). `game?.id` is the stable check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id]);

  useEffect(() => {
    const handler = () => setShowOnboarding(true);
    window.addEventListener("ac:show-onboarding", handler);
    return () => window.removeEventListener("ac:show-onboarding", handler);
  }, []);

  // Detect newly-obtained "special" cards (foil OR rarity >= rare) and queue them for reveal.
  // Uses a ref (not state) for seenCardIds to avoid re-running the effect recursively.
  useEffect(() => {
    if (!game?.inventory) return;
    const all = [
      ...(game.inventory.allies || []),
      ...(game.inventory.objects || []),
      ...(game.inventory.abilities || []),
    ];
    const currentIds = new Set(all.map(c => c.id));
    if (!primedRef.current) {
      // First load: prime the set without triggering reveal
      seenCardIdsRef.current = currentIds;
      primedRef.current = true;
      return;
    }
    const prev = seenCardIdsRef.current || new Set();
    const specialNew = all.filter(c =>
      !prev.has(c.id) &&
      (c.foil || ["rare", "epic", "legendary"].includes((c.rarity || "").toLowerCase()))
    );
    if (specialNew.length > 0) {
      setRevealQueue(q => [...q, ...specialNew]);
    }
    seenCardIdsRef.current = currentIds;
  }, [game?.inventory]);

  // Fetch daily limit
  useEffect(() => {
    const fetchLimit = async () => {
      try {
        const res = await axios.get(`${API}/daily-limit`, { headers: { Authorization: `Bearer ${token}` } });
        setDailyLimit(res.data);
      } catch {}
    };
    if (token) fetchLimit();
  }, [API, token, game?.story_pages?.length]);

  // Check payment return URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const payment = params.get("payment");
    if (sessionId && payment === "success") {
      const pollStatus = async (attempts = 0) => {
        try {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data.payment_status === "paid") {
            toast.success(t(game?.language || "en", "payment_success"));
            loadGame(gameId);
            window.history.replaceState({}, "", window.location.pathname);
          } else if (attempts < 5) {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch {}
      };
      pollStatus();
    } else if (payment === "cancelled") {
      toast.error(t(game?.language || "en", "payment_cancelled"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  // Check payment return URL — runs once on mount; the URL params are
  // read directly from window.location and shouldn't trigger re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Music context switching — adaptive (boss vs battle, exploration uses world_clock)
  useEffect(() => {
    if (!game) return;
    const battle = game.battle_state;
    const hasBattleActive = battle && battle.active;
    if (hasBattleActive) {
      // Boss fights have their own dedicated mood
      const isBoss = !!(battle.enemy && battle.enemy.is_boss);
      ambientMusic.play(isBoss ? 'boss' : 'battle');
    } else if (activeTab === "story") {
      ambientMusic.play('exploration', { world_clock: game.world_clock });
    }
  }, [game?.battle_state?.active, game?.battle_state?.enemy?.is_boss, game?.world_clock?.time_of_day, game?.world_clock?.season, game, activeTab]);

  if (error && !game) {
    const uiLang = localStorage.getItem("ac_ui_lang") || "it";
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0c] p-4 text-center">
        <AlertTriangle size={48} className="text-red-500 mb-4 animate-bounce" />
        <h2 className="font-heading text-2xl text-[#cfb53b] mb-2">
          {uiLang.startsWith("it") ? 'Errore di Caricamento' : 'Loading Error'}
        </h2>
        <p className="text-red-300 max-w-md mb-6">{error}</p>
        <div className="flex gap-4">
          <Button onClick={() => loadGame(gameId)} className="bg-[#cfb53b] hover:bg-[#d4af37] text-black font-heading font-bold px-6">
            {uiLang.startsWith("it") ? 'Riprova' : 'Retry'}
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} className="border-[#cfb53b]/20 text-[#cfb53b] hover:bg-[#cfb53b]/10 font-heading px-6">
            {uiLang.startsWith("it") ? 'Torna al Menu' : 'Back to Menu'}
          </Button>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-heading text-[#cfb53b]">Loading...</p>
        </div>
      </div>
    );
  }

  const lang = game.language || "en";
  const char = game.character;
  const hasBattle = game.battle_state && game.battle_state.active;
  const hpPercent = (char.hp / char.max_hp) * 100;
  const hpColor = hpPercent > 60 ? "high" : hpPercent > 30 ? "medium" : "";

  const primaryTabs = [
    { id: "story", label: t(lang, 'story'), icon: BookOpen },
    { id: "inventory", label: t(lang, 'inventory'), icon: Backpack },
    { id: "collection", label: t(lang, 'collection'), icon: Library },
    { id: "shop", label: t(lang, 'shop'), icon: Coins },
    { id: "trade", label: t(lang, 'trade'), icon: Handshake },
    { id: "arena", label: t(lang, 'arena'), icon: Shield },
  ];
  const moreTabs = [
    { id: "fusion", label: t(lang, 'fusion'), icon: Sparkles },
    { id: "grimoire", label: t(lang, 'grimoire'), icon: ScrollText },
    { id: "bestiary", label: t(lang, 'bestiary'), icon: Skull },
    { id: "map", label: t(lang, 'map'), icon: Map },
    { id: "dashboard", label: t(lang, 'dashboard'), icon: BarChart3 },
    { id: "achievements", label: t(lang, 'achievements'), icon: Trophy },
  ];
  const isMoreActive = moreTabs.some(t => t.id === activeTab);

  // Map tab id → badge count. Returns 0 / undefined for tabs without badges.
  const badgeFor = (id) => {
    if (id === "trade") return counts.trade;
    if (id === "grimoire") return 0;
    if (id === "inventory") return counts.inventory;
    return 0;
  };
  const fmtBadge = (n) => (n > 99 ? "99+" : String(n));

  if (hasBattle) {
    return <BattleArena />;
  }

  return (
    <div className="h-screen bg-[#0a0a0c] flex flex-col overflow-hidden" data-testid="game-view">
      {/* Top HUD */}
      <div className="bg-[#1a1a1d]/90 border-b border-[#cfb53b]/20 px-2 sm:px-4 py-2 sm:py-3 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              data-testid="back-to-menu"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-[#94a3b8] hover:text-[#cfb53b] h-8 w-8 p-0 sm:h-auto sm:w-auto sm:p-2"
            >
              <ArrowLeft size={18} />
            </Button>
            <div className="min-w-0 flex-1">
              <h2 className="font-heading text-sm sm:text-lg text-[#cfb53b] leading-none flex items-center gap-1.5 sm:gap-2 min-w-0">
                <span className="truncate min-w-0">{char.name}</span>
                {game.tag && (
                  <span
                    data-testid="game-tag"
                    title={lang.startsWith("it") ? "Tag partita (per scambi carte)" : "Game tag (for card trading)"}
                    className="shrink-0 text-[9px] sm:text-[10px] font-mono tracking-wider bg-[#cfb53b]/15 text-[#cfb53b] px-1.5 py-0.5 rounded border border-[#cfb53b]/30 cursor-pointer hover:bg-[#cfb53b]/25"
                    onClick={() => {
                      const value = game.tag;
                      const okToast = () => toast.success(lang.startsWith("it") ? `Copiato ${value}` : `Copied ${value}`);
                      const failToast = () => toast.message(value, { description: lang.startsWith("it") ? "Copia manuale: tocca e tieni premuto" : "Manual copy: tap and hold to copy" });
                      // Try async Clipboard API first
                      if (navigator.clipboard && window.isSecureContext) {
                        navigator.clipboard.writeText(value).then(okToast).catch(() => {
                          // Fall through to execCommand fallback
                          tryExecCopy();
                        });
                      } else {
                        tryExecCopy();
                      }
                      function tryExecCopy() {
                        try {
                          const ta = document.createElement("textarea");
                          ta.value = value;
                          ta.setAttribute("readonly", "");
                          ta.style.position = "fixed";
                          ta.style.top = "0";
                          ta.style.left = "0";
                          ta.style.opacity = "0";
                          document.body.appendChild(ta);
                          ta.select();
                          const ok = document.execCommand("copy");
                          document.body.removeChild(ta);
                          if (ok) okToast(); else failToast();
                        } catch { failToast(); }
                      }
                    }}
                  >{game.tag}</span>
                )}
              </h2>
              <p className="hidden min-[400px]:block text-[10px] sm:text-xs text-[#94a3b8] capitalize">
                {char.class_type} - {t(lang, 'level')} {char.level}
                <span className="hidden sm:inline text-[#cfb53b]/60"> | {tf(lang, 'chapter_num', [game.current_chapter || 1])}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-5 shrink-0">
            {/* HP */}
            <div className="flex items-center gap-1 sm:gap-2" data-testid="player-hp">
              <Heart size={14} className="text-red-500 sm:w-4 sm:h-4 shrink-0" />
              <div className="w-10 sm:w-28">
                <div className="hp-bar h-2 sm:h-3">
                  <div className={`hp-bar-fill ${hpColor}`} style={{ width: `${hpPercent}%` }} />
                </div>
                <p className="hidden sm:block text-[9px] sm:text-xs text-[#94a3b8] text-center">{char.hp}/{char.max_hp}</p>
              </div>
            </div>
            {/* XP - hide on mobile */}
            <div className="hidden md:flex items-center gap-2" data-testid="player-xp">
              <Sparkles size={14} className="text-[#cfb53b]" />
              <span className="text-xs text-[#94a3b8]">{t(lang, 'xp')}: {char.xp}/{char.level * 1000}</span>
            </div>
            {/* Gold */}
            <div className="flex items-center gap-1" data-testid="player-gold">
              <Coins size={14} className="text-[#cfb53b] shrink-0" />
              <span className="text-[10px] sm:text-xs text-[#cfb53b] font-heading">{char.gold || 0}</span>
            </div>
            {/* Daily Pages */}
            {dailyLimit && (
              <div className="hidden sm:flex items-center gap-1" data-testid="daily-limit-badge">
                {dailyLimit.is_premium ? (
                  <span className="text-[9px] bg-[#cfb53b]/20 text-[#cfb53b] px-1.5 py-0.5 rounded-full font-heading">{t(lang, 'premium_user')}</span>
                ) : (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-heading ${dailyLimit.pages_remaining > 3 ? 'bg-green-500/15 text-green-400' : dailyLimit.pages_remaining > 0 ? 'bg-yellow-500/15 text-yellow-400' : 'bg-red-500/15 text-red-400'}`}>
                    {dailyLimit.pages_remaining}/{dailyLimit.daily_limit}
                  </span>
                )}
              </div>
            )}
            {/* Daily Quests */}
            <DailyQuests />
            <SettingsPanel
              gameMode
              gameLang={game.language}
              gameId={game.id}
              currentImageStyle={game.image_style}
              onImageStyleChange={(newStyle) => {
                // Update only the image_style field locally to avoid reloading the
                // whole game (which could disrupt an in-progress story choice
                // and cause mobile scroll/rendering side effects). The new style
                // applies from the next AI generation forward.
                setGame((prev) => (prev ? { ...prev, image_style: newStyle } : prev));
              }}
              characterAppearance={char?.appearance}
              onAppearanceChange={(newAppearance) => {
                setGame((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    character: {
                      ...prev.character,
                      appearance: newAppearance
                    }
                  };
                });
              }}
            />
          </div>
        </div>
      </div>

      {/* AI status banner: only visible when image generation is degraded */}
      <AIStatusBanner lang={lang} />

      {/* Navigation Tabs */}
      <div className="bg-[#0a0a0c] border-b border-[#2d3748] px-2 sm:px-4 relative">
        <div className="max-w-6xl mx-auto flex" data-testid="game-tabs-nav">
          <div className="flex overflow-x-auto scrollbar-hide flex-1 min-w-0">
            {primaryTabs.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              const badgeN = badgeFor(tab.id);
              return (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => { setActiveTab(tab.id); setMoreOpen(false); }}
                  className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-heading transition-all border-b-2 whitespace-nowrap shrink-0 justify-center ${
                    active
                      ? 'text-[#cfb53b] border-[#cfb53b] bg-[#cfb53b]/5'
                      : 'text-[#94a3b8] border-transparent hover:text-[#e2e8f0] hover:border-[#cfb53b]/30'
                  }`}
                >
                  <Icon size={14} className="shrink-0 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {badgeN > 0 && (
                    <span
                      data-testid={`tab-badge-${tab.id}`}
                      className="absolute -top-0.5 right-0 sm:right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none rounded-full bg-red-500 text-white border border-[#0a0a0c] shadow-[0_0_0_1px_rgba(239,68,68,0.4)]"
                    >
                      {fmtBadge(badgeN)}
                    </span>
                  )}
                </button>
              );
            })}

            {hasBattle && (
              <button
                data-testid="tab-battle"
                onClick={() => setActiveTab("battle")}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-heading text-red-400 border-b-2 border-red-500 bg-red-500/5 animate-pulse shrink-0 justify-center"
              >
                <Swords size={14} className="shrink-0" /> <span className="hidden sm:inline">{t(lang, 'battle_alert')}</span>
              </button>
            )}
          </div>

          {/* "Altro" / More dropdown — outside scrollable area to avoid clipping */}
          <div className="relative shrink-0 border-l border-[#2d3748]">
            <button
              data-testid="tab-more"
              onClick={() => setMoreOpen(v => !v)}
              className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-heading transition-all border-b-2 whitespace-nowrap justify-center h-full ${
                isMoreActive || moreOpen
                  ? 'text-[#cfb53b] border-[#cfb53b] bg-[#cfb53b]/5'
                  : 'text-[#94a3b8] border-transparent hover:text-[#e2e8f0] hover:border-[#cfb53b]/30'
              }`}
            >
              <MoreHorizontal size={14} className="shrink-0 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{lang.startsWith("it") ? "Altro" : "More"}</span>
              {counts.more > 0 && (
                <span
                  data-testid="tab-badge-more"
                  className="absolute -top-0.5 right-0 sm:right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none rounded-full bg-red-500 text-white border border-[#0a0a0c] shadow-[0_0_0_1px_rgba(239,68,68,0.4)]"
                >
                  {fmtBadge(counts.more)}
                </span>
              )}
            </button>
            {moreOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMoreOpen(false)}
                  data-testid="tab-more-backdrop"
                />
                <div
                  className="absolute right-0 top-full mt-1 w-48 bg-[#1a1a1d] border border-[#cfb53b]/40 rounded-md shadow-2xl z-50 overflow-hidden"
                  data-testid="tab-more-menu"
                >
                  {moreTabs.map(tab => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.id;
                    const badgeN = badgeFor(tab.id);
                    return (
                      <button
                        key={tab.id}
                        data-testid={`more-item-${tab.id}`}
                        onClick={() => { setActiveTab(tab.id); setMoreOpen(false); }}
                        className={`relative w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-heading transition-colors text-left ${
                          active
                            ? 'text-[#cfb53b] bg-[#cfb53b]/10'
                            : 'text-[#cbd5e1] hover:bg-[#0a0a0c] hover:text-[#cfb53b]'
                        }`}
                      >
                        <Icon size={15} className="shrink-0" />
                        <span className="flex-1">{tab.label}</span>
                        {badgeN > 0 && (
                          <span
                            data-testid={`more-item-badge-${tab.id}`}
                            className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none rounded-full bg-red-500 text-white"
                          >
                            {fmtBadge(badgeN)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0 pb-24 sm:pb-6">
        <LevelUpModal />
        {activeTab === "story" && <StoryBook />}
        {activeTab === "inventory" && <Inventory />}
        {activeTab === "collection" && (
          <Collection
            onTradeCard={(cardId, category, tradeType) => {
              setPendingTrade({ card_id: cardId, category, type: tradeType });
              setActiveTab("trade");
            }}
          />
        )}
        {activeTab === "shop" && <Shop />}
        {activeTab === "fusion" && <Fusion />}
        {activeTab === "grimoire" && <Grimoire />}
        {activeTab === "bestiary" && <Bestiary onReplayStarted={() => setActiveTab("story")} />}
        {activeTab === "map" && <WorldMap onTravelComplete={() => setActiveTab("story")} />}
        {activeTab === "dashboard" && <Dashboard />}
        {activeTab === "trade" && (
          <Trade
            initialSelection={pendingTrade}
            onSelectionConsumed={() => setPendingTrade(null)}
          />
        )}
        {activeTab === "arena" && <Arena />}
        {activeTab === "achievements" && <Achievements />}
      </div>

      {/* Global card reveal overlay */}
      <CardRevealOverlay
        cards={revealQueue}
        lang={game?.language || "en"}
        onClose={() => setRevealQueue([])}
      />

      {/* First-time onboarding tour (re-playable from Settings) */}
      <OnboardingTour
        open={showOnboarding}
        lang={game?.language || "en"}
        onClose={() => setShowOnboarding(false)}
      />

      {/* Nyx Animated Mascot */}
      <NyxMascot />
    </div>
  );
}

export default function GameView() {
  return (
    <GameProvider>
      <GameContent />
    </GameProvider>
  );
}
